import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BoxedTitle from "@/components/BoxedTitle";
import useAdminIdeas from "@/pages/admin/ideas/useAdminIdeas";
import { clearCache } from "@/pages/admin/adminCache";

const KEY_STORAGE = "vbb_admin_key";

const STATUS_STYLES = {
  backlog: "bg-white text-[#0a0a0a] border border-[#0a0a0a]",
  selected: "bg-[#0a0a0a] text-white",
  drafting: "bg-[#e34234] text-white",
  shelved: "bg-white text-[#6b6b6b] border border-[#c9c9c9]",
  merged: "bg-white text-[#6b6b6b] border border-[#c9c9c9] italic",
};

const STATUS_LABELS = {
  backlog: "Backlog",
  selected: "Selected",
  drafting: "Drafting",
  shelved: "Shelved",
  merged: "Merged",
};

const KIND_LABELS = {
  story: "Story",
  essay: "Essay",
  either: "Either",
};

/**
 * /admin/ideas — private idea backlog for stories and essays. Not
 * surfaced anywhere public. Filter by status and by kind. "Promote to
 * essay" spawns a new draft essay pre-seeded with the idea contents.
 */
export default function AdminIdeas() {
  const navigate = useNavigate();
  const { key, ideas, status, deleteIdea } = useAdminIdeas();
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!key || status.state === "unauthorized") {
      navigate("/login?next=/admin/ideas", { replace: true });
    }
  }, [key, status.state, navigate]);

  const onLock = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    clearCache();
    navigate("/login?next=/admin/ideas", { replace: true });
  };

  const filtered = ideas.filter((i) => {
    if (statusFilter !== "all" && i.status !== statusFilter) return false;
    if (kindFilter !== "all" && i.kind !== kindFilter) return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      const hay = `${i.title} ${i.summary || ""} ${i.notes || ""} ${(i.tags || []).join(" ")}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const statusCounts = ideas.reduce(
    (acc, i) => ({ ...acc, [i.status]: (acc[i.status] || 0) + 1 }),
    {}
  );

  if (!key) return null;

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="admin-ideas-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="vbb-overline">
            <Link to="/admin" className="vbb-link" data-testid="crumb-admin">
              Admin
            </Link>{" "}
            / Ideas
          </div>
          <h1
            className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
            data-testid="admin-ideas-title"
          >
            <BoxedTitle size="lg" thick>
              Ideas.
            </BoxedTitle>
          </h1>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-[#6b6b6b]">
            Private backlog. Story and essay ideas. Promote an idea to a
            draft essay when you&apos;re ready to work on it.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-back-to-home"
          >
            ← Admin
          </Link>
          <Link
            to="/admin/ideas/capture"
            className="vbb-btn vbb-btn-ghost"
            data-testid="ideas-capture-btn"
          >
            + Capture
          </Link>
          <Link
            to="/admin/ideas/new"
            className="vbb-btn"
            data-testid="ideas-new-btn"
          >
            New idea
          </Link>
          <button
            onClick={onLock}
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-lock-btn"
          >
            Lock
          </button>
        </div>
      </div>

      {/* Status filters */}
      <div
        className="mt-8 flex flex-wrap gap-2"
        data-testid="idea-status-filters"
      >
        {[
          { id: "all", label: `All (${ideas.length})` },
          { id: "backlog", label: `Backlog (${statusCounts.backlog || 0})` },
          { id: "selected", label: `Selected (${statusCounts.selected || 0})` },
          { id: "drafting", label: `Drafting (${statusCounts.drafting || 0})` },
          { id: "shelved", label: `Shelved (${statusCounts.shelved || 0})` },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.id)}
            data-testid={`idea-status-${f.id}`}
            className={`px-4 py-2 font-mono-tiny uppercase tracking-wider border ${
              statusFilter === f.id
                ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                : "bg-white text-[#0a0a0a] border-[#0a0a0a] hover:bg-[#fafafa]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Kind filters */}
      <div
        className="mt-3 flex flex-wrap gap-2 items-center"
        data-testid="idea-kind-filters"
      >
        <span className="font-mono-tiny text-[#6b6b6b]">Kind:</span>
        {["all", "story", "essay", "either"].map((k) => (
          <button
            key={k}
            onClick={() => setKindFilter(k)}
            data-testid={`idea-kind-${k}`}
            className={`px-3 py-1 font-mono-tiny uppercase tracking-wider border ${
              kindFilter === k
                ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                : "bg-white text-[#0a0a0a] border-[#0a0a0a] hover:bg-[#fafafa]"
            }`}
          >
            {k === "all" ? "All" : KIND_LABELS[k]}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mt-4 flex items-center gap-3">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search titles, summaries, notes, tags…"
          className="w-full sm:max-w-md border border-[#0a0a0a] p-2 text-[14px] focus:outline-none focus:border-[#e34234]"
          data-testid="idea-search-input"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="font-mono-tiny text-[12px] text-[#6b6b6b] hover:text-[#e34234]"
            data-testid="idea-search-clear"
          >
            clear
          </button>
        )}
        <span
          className="font-mono-tiny text-[12px] text-[#6b6b6b] whitespace-nowrap"
          data-testid="idea-visible-count"
        >
          {filtered.length} shown
        </span>
      </div>

      {status.state === "error" && (
        <div
          className="mt-8 p-4 border border-[#e34234] text-[#e34234] font-mono-tiny"
          data-testid="admin-error-banner"
        >
          {status.error}
        </div>
      )}

      <div className="mt-8" data-testid="idea-list">
        {status.state === "ready" && filtered.length === 0 && (
          <div
            className="p-10 text-center font-mono-tiny text-[#6b6b6b] border border-[#0a0a0a]"
            data-testid="idea-empty-state"
          >
            {ideas.length === 0
              ? "No ideas yet. Add one — or drop your 4 idea files in chat and I'll import them."
              : "No ideas match these filters."}
          </div>
        )}
        {filtered.map((idea) => (
          <article
            key={idea.id}
            className="border border-[#0a0a0a] p-6 bg-white mb-4"
            data-testid={`idea-row-${idea.id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className={`px-2 py-0.5 font-mono-tiny uppercase tracking-wider ${STATUS_STYLES[idea.status] || STATUS_STYLES.backlog}`}
                    data-testid={`idea-status-badge-${idea.id}`}
                  >
                    {STATUS_LABELS[idea.status] || idea.status}
                  </span>
                  <span
                    className="font-mono-tiny uppercase tracking-wider text-[#6b6b6b]"
                    data-testid={`idea-kind-badge-${idea.id}`}
                  >
                    {KIND_LABELS[idea.kind] || idea.kind}
                  </span>
                  {idea.essay_id && (
                    <Link
                      to={`/admin/essays/${idea.essay_id}`}
                      className="font-mono-tiny vbb-link"
                      data-testid={`idea-essay-link-${idea.id}`}
                    >
                      → linked essay
                    </Link>
                  )}
                </div>
                <h2 className="font-display text-xl mt-2 leading-tight">
                  <Link
                    to={`/admin/ideas/${idea.id}`}
                    className="hover:text-[#e34234] transition-colors"
                    data-testid={`idea-edit-link-${idea.id}`}
                  >
                    {idea.title}
                  </Link>
                </h2>
                {idea.summary && (
                  <p className="mt-2 text-[15px] leading-relaxed text-[#2a2a2a]">
                    {idea.summary}
                  </p>
                )}
                {idea.tags && idea.tags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {idea.tags.map((t) => (
                      <span
                        key={t}
                        className="font-mono-tiny text-[11px] px-2 py-0.5 border border-[#c9c9c9] text-[#6b6b6b]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to={`/admin/ideas/${idea.id}`}
                  className="vbb-btn vbb-btn-ghost"
                  data-testid={`idea-open-btn-${idea.id}`}
                >
                  Edit
                </Link>
                <button
                  type="button"
                  onClick={async () => {
                    if (window.confirm(`Delete "${idea.title}"?`)) {
                      await deleteIdea(idea.id);
                    }
                  }}
                  className="vbb-btn vbb-btn-ghost text-[#e34234] border-[#e34234]"
                  data-testid={`idea-delete-btn-${idea.id}`}
                >
                  Delete
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </main>
  );
}
